import { createAction } from 'redux-starter-kit';
import {
  requestFailed, requestStart, requestSucceded,
} from '../../domain/requests/RequestActions';
import { get, post } from '../../../utility/via-fetch';
import { putAllInspections, putInspections } from '../../domain/inspections/inspectionListActions';
import { addMessage } from '../application/messageUiActions';
import {
  INSPECTION_LIST_TAG_COLOUR_ALL,
  INSPECTION_LIST_TAG_COLOUR_MY,
} from '../../../sections/secured/dashboard/dashboard-util';
import {getDateComparisonNumber} from "../../../sections/secured/bynx/packageUtil";


/**
 * An action to mark the setting of load parameters
 */
export const setLoadParameters = createAction('SET_INSPECTION_LIST_LOAD_PARAMETERS');

/**
 * An action to set the load parameters if required
 */
export function setLoadingParameterValues(filterObj) {
  return async (dispatch) => {
    dispatch(requestStart(setLoadParameters.toString()));
    dispatch(setLoadParameters(filterObj));
  };
}

/**
 * An action that signifies a request to filter inspections has been made.
 */
export const filterInspectionListPageData = createAction('FILTER_INSPECTION_LIST_PAGE_DATA');

/**
 * An action for filtering loaded data (for action on the page)
 */
export function filterListPageData(inspections, allInspections, filterParameters) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(filterInspectionListPageData.toString()));
    dispatch(filterInspectionListPageData(requestId));

    try {
      // Check for same click - do not switch off all or my inspections
      if (
        (filterParameters.allTagColour && filterParameters.newColour === INSPECTION_LIST_TAG_COLOUR_ALL)
        || (filterParameters.myTagColour && filterParameters.newColour === INSPECTION_LIST_TAG_COLOUR_MY)
      ) {
        dispatch(requestSucceded(requestId));
      } else {
        // data filtered on re-render (below)

        // Change tag (and others)
        if (filterParameters.colourFunction) {
          filterParameters.colourFunction(filterParameters.turningOn ? filterParameters.newColour : null);
        }
        if (filterParameters.tagControl) {
          filterParameters.tagControl(filterParameters.reset, filterParameters);
        }
      }
    } catch (error) {
      dispatch(requestFailed(requestId, error));
      dispatch(addMessage('error', 'inspectionListFilterError'));
    }
  };
}

/**
 * An action for filtering loaded data (on first load - from dashboard AND on list page when change filters - re-renders)
 */
function filterListPageDataOnLoad(inspections, loadParams, userId) {
  // Filter data
  try {
    // No longer does dates here - now part of date retrieve

    // Loop inspections
    return inspections
      .filter((inspection) => {
        // All
        let keepInspection = true;

        // My
        if (loadParams.my && inspection.inspectorId !== userId) {
          keepInspection = false;
        }

        // Compliance
        if (keepInspection && loadParams.compliance && !inspection.complianceIndicator) {
          keepInspection = false;
        }

        // Others
        if (keepInspection && loadParams.other && inspection.complianceIndicator) {
          keepInspection = false;
        }

        // Defects
        if (keepInspection && loadParams.defects
           && (inspection.noChecklistFaults === 0 && inspection.noNewDamages === 0)) {
          keepInspection = false;
        }

        // No Defects
        if (keepInspection && loadParams.noDefects
            && (inspection.noChecklistFaults > 0 || inspection.noNewDamages > 0
              || inspection.currencyCode === '(MULTIPLE)' || inspection.totalEstimatedRepairCost > 0)) {
          keepInspection = false;
        }

        // Checklist Faults
        if (keepInspection && loadParams.faults && inspection.noChecklistFaults < 1) {
          keepInspection = false;
        }

        // New Damage
        if (keepInspection && loadParams.damages && inspection.noNewDamages < 1) {
          keepInspection = false;
        }

        // Costs
        if (keepInspection && loadParams.costs && inspection.totalEstimatedRepairCost === 0) {
          keepInspection = false;
        }

        return keepInspection;
      });
  } catch (error) {
    return inspections;
  }
}

/**
 * An action that signifies a request to load inspections has been made.
 */
export const fetchInspectionListPageData = createAction('FETCH_INSPECTION_LIST_PAGE_DATA');

/**
 * An action for loading all the data for the inspection list page.
 */
export function requestInspectionListPageData(loadParams, userId) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchInspectionListPageData.toString()));
    dispatch(fetchInspectionListPageData(requestId));
    try {
      let inspections;

      // Inspections restricted by date
      if ( loadParams && loadParams.dates && loadParams.dates.length > 1) {
        const fromDate = new Date(loadParams.dates[0]);
        const toDate = new Date(loadParams.dates[1]);
        const compareFrom = getDateComparisonNumber(fromDate);
        const compareTo = getDateComparisonNumber(toDate);
        inspections = await get('/api/inspections/list/from/' + compareFrom + '/to/' + compareTo);
      }

      // All Inspections
      else {
        inspections = await get('/api/inspections/list/');
      }

      // Store retrieved data and filter if required
      dispatch(putAllInspections(JSON.parse(JSON.stringify(inspections)))); // copy of all inspections for later filtering
      if (loadParams && loadParams !== {}) {
        inspections = filterListPageDataOnLoad(inspections, loadParams, userId); // no longer does dates  *****
      }
      dispatch(putInspections(inspections));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}


/**
 * An action that signifies a request to download inspections has been made.
 */
export const fetchInspectionListCSV = createAction('FETCH_INSPECTION_LIST_CSV');

/**
 * An action for downloading the list of inspections currently displayed on the inspections list page
 */
export function requestInspectionListCSV(downloadQuery, filename) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchInspectionListCSV.toString()));
    dispatch(fetchInspectionListCSV(requestId));
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    try {
      const workbook = await post('/api/inspections/list/download', {}, { ...downloadQuery, timezone });

      // This is a method of forcing a browser download of a blob you have stored in client memory.
      // If the above method was a get, we could simply use a normal a tag to download the file.
      // However since it isn't we have to jump through some hoops.
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(workbook);
      link.download = `${filename}.xlsx`;
      link.click();

      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}
